import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	CheckCircle2,
	Download,
	FileDown,
	FolderOpen,
	Loader2,
	Plus,
	RefreshCw,
	Trash2,
	XCircle,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/features/auth/use-auth";
import { apiFetch, getAccessToken } from "@/lib/auth-api";
import { ensureElementVisible } from "@/lib/route-guards";

export const Route = createFileRoute("/_auth/konfiguracja/iqvia")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(context.queryClient, "sidebar.system.iqvia", location.pathname);
	},
	component: IqviaConfigPage,
});

// ─── Types ──────────────────────────────────────────────────────────────────────

interface IqviaSource {
	id: string;
	label: string;
	reportPath: string;
	scheduleHours: number;
}

interface IqviaAccount {
	id: string;
	label: string;
	baseUrl: string;
	username: string;
	acceptInvalidCerts?: boolean;
	sources: IqviaSource[];
}

interface IqviaConfig {
	accounts: IqviaAccount[];
}

interface SettingsResponse {
	key: string;
	value: IqviaConfig | null;
}

interface SyncStatusEntry {
	accountId: string;
	sourceId: string;
	lastSyncAt: string | null;
	lastResult: string | null;
}

interface IqviaFile {
	id: string;
	name: string;
	size: number;
	uploadStamp: string;
	alreadyDownloaded: boolean;
	downloadedAt?: string;
}

interface DownloadEntry {
	id: number;
	accountId: string;
	sourceId: string;
	fileName: string;
	downloadedAt: string;
	downloadedBy: string;
	status: string;
	errorMessage?: string;
}

const DEFAULT_BASE_URL = "https://secure-transfer.solutions.iqvia.com";

const SCHEDULE_OPTIONS = [
	{ value: "6", label: "Co 6h" },
	{ value: "12", label: "Co 12h" },
	{ value: "24", label: "Co 24h" },
	{ value: "48", label: "Co 48h" },
	{ value: "0", label: "Wylaczony" },
];

// ─── Helpers ────────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
	if (bytes === 0) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${(bytes / k ** i).toFixed(1)} ${sizes[i]}`;
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleString("pl-PL");
}

async function downloadFileFromApi(
	fileId: string,
	fileName: string,
	accountId: string,
	sourceId: string,
) {
	const token = getAccessToken();
	const params = new URLSearchParams({
		account_id: accountId,
		source_id: sourceId,
		file_name: fileName,
	});
	const res = await fetch(
		`/api/admin/iqvia/files/${encodeURIComponent(fileId)}/download?${params}`,
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
	if (!res.ok) throw new Error(`Download failed: ${res.status}`);
	const blob = await res.blob();
	const url = URL.createObjectURL(blob);
	try {
		const a = document.createElement("a");
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	} finally {
		URL.revokeObjectURL(url);
	}
}

/** Debounce hook — returns a function that delays calling `fn` until `delay` ms after last call */
function useDebouncedSave(saveFn: (accounts: IqviaAccount[]) => void, delay = 800) {
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	return useCallback(
		(accounts: IqviaAccount[]) => {
			if (timerRef.current) clearTimeout(timerRef.current);
			timerRef.current = setTimeout(() => saveFn(accounts), delay);
		},
		[saveFn, delay],
	);
}

// ─── Main Page ──────────────────────────────────────────────────────────────────

function IqviaConfigPage() {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const isSuperAdmin = user?.roles?.includes("super_admin");

	const [deleteTarget, setDeleteTarget] = useState<{
		type: "account" | "source";
		accountId: string;
		sourceId?: string;
	} | null>(null);

	// ─── Config query ───────────────────────────────────────────────────────────

	const configQuery = useQuery({
		queryKey: ["settings", "iqvia_moveit_config"],
		queryFn: () => apiFetch<SettingsResponse>("/admin/settings/iqvia_moveit_config"),
		enabled: !!isSuperAdmin,
	});

	const serverAccounts: IqviaAccount[] = configQuery.data?.value?.accounts ?? [];

	// Local state for editing (prevents keystroke-per-save)
	const [localAccounts, setLocalAccounts] = useState<IqviaAccount[]>([]);
	const [initialized, setInitialized] = useState(false);

	// Sync server → local on first load or when server data changes
	useEffect(() => {
		if (serverAccounts.length > 0 && !initialized) {
			setLocalAccounts(serverAccounts);
			setInitialized(true);
		}
	}, [serverAccounts, initialized]);

	const doSaveConfig = useCallback(
		(newAccounts: IqviaAccount[]) => {
			apiFetch("/admin/settings/iqvia_moveit_config", {
				method: "PUT",
				body: JSON.stringify({ value: { accounts: newAccounts } }),
			})
				.then(() => {
					queryClient.invalidateQueries({ queryKey: ["settings", "iqvia_moveit_config"] });
				})
				.catch((e: unknown) => {
					console.error("Failed to save IQVIA config:", e);
				});
		},
		[queryClient],
	);

	const debouncedSave = useDebouncedSave(doSaveConfig);

	const updateAccount = useCallback(
		(accountId: string, patch: Partial<IqviaAccount>) => {
			const updated = localAccounts.map((a) => (a.id === accountId ? { ...a, ...patch } : a));
			setLocalAccounts(updated);
			debouncedSave(updated);
		},
		[localAccounts, debouncedSave],
	);

	const addAccount = useCallback(() => {
		const newId = `NEW_${Date.now()}`;
		const newAccount: IqviaAccount = {
			id: newId,
			label: "",
			baseUrl: DEFAULT_BASE_URL,
			username: "",
			sources: [],
		};
		const updated = [...localAccounts, newAccount];
		setLocalAccounts(updated);
		doSaveConfig(updated);
	}, [localAccounts, doSaveConfig]);

	const removeAccount = useCallback(
		(accountId: string) => {
			const updated = localAccounts.filter((a) => a.id !== accountId);
			setLocalAccounts(updated);
			doSaveConfig(updated);
			// Delete orphaned password secret
			apiFetch(`/admin/settings/iqvia_account_${accountId}`, {
				method: "PUT",
				body: JSON.stringify({ value: {} }),
			}).catch(() => {});
		},
		[localAccounts, doSaveConfig],
	);

	const addSource = useCallback(
		(accountId: string) => {
			const newSource: IqviaSource = {
				id: `src_${Date.now()}`,
				label: "",
				reportPath: "",
				scheduleHours: 24,
			};
			const account = localAccounts.find((a) => a.id === accountId);
			if (!account) return;
			const updated = localAccounts.map((a) =>
				a.id === accountId ? { ...a, sources: [...a.sources, newSource] } : a,
			);
			setLocalAccounts(updated);
			doSaveConfig(updated);
		},
		[localAccounts, doSaveConfig],
	);

	const updateSource = useCallback(
		(accountId: string, sourceId: string, patch: Partial<IqviaSource>) => {
			const account = localAccounts.find((a) => a.id === accountId);
			if (!account) return;
			const updatedSources = account.sources.map((s) =>
				s.id === sourceId ? { ...s, ...patch } : s,
			);
			const updated = localAccounts.map((a) =>
				a.id === accountId ? { ...a, sources: updatedSources } : a,
			);
			setLocalAccounts(updated);
			debouncedSave(updated);
		},
		[localAccounts, debouncedSave],
	);

	const removeSource = useCallback(
		(accountId: string, sourceId: string) => {
			const account = localAccounts.find((a) => a.id === accountId);
			if (!account) return;
			const updated = localAccounts.map((a) =>
				a.id === accountId ? { ...a, sources: a.sources.filter((s) => s.id !== sourceId) } : a,
			);
			setLocalAccounts(updated);
			doSaveConfig(updated);
		},
		[localAccounts, doSaveConfig],
	);

	// ─── Sync status ───────────────────────────────────────────────────────────

	const syncStatusQuery = useQuery({
		queryKey: ["iqvia", "sync-status"],
		queryFn: () => apiFetch<SyncStatusEntry[]>("/admin/iqvia/sync-status"),
		enabled: !!isSuperAdmin && localAccounts.length > 0,
		refetchInterval: 30_000,
	});

	const getSyncStatus = (accountId: string, sourceId: string) =>
		syncStatusQuery.data?.find((s) => s.accountId === accountId && s.sourceId === sourceId);

	// ─── Delete confirmation ────────────────────────────────────────────────────

	const confirmDelete = () => {
		if (!deleteTarget) return;
		if (deleteTarget.type === "account") {
			removeAccount(deleteTarget.accountId);
		} else if (deleteTarget.sourceId) {
			removeSource(deleteTarget.accountId, deleteTarget.sourceId);
		}
		setDeleteTarget(null);
	};

	if (!isSuperAdmin) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">Brak dostepu. Wymagana rola super_admin.</p>
			</div>
		);
	}

	const hasAnySources = localAccounts.some((a) => a.sources.length > 0);
	// Check if account was already saved to server (exists in serverAccounts)
	const isAccountSaved = (accountId: string) => serverAccounts.some((a) => a.id === accountId);

	return (
		<div className="p-6 max-md:p-3">
			<div className="max-w-3xl">
				<h1 className="text-2xl font-semibold mb-6 max-md:hidden">Konfiguracja IQVIA MOVEit</h1>

				{localAccounts.map((account) => (
					<AccountCard
						key={account.id}
						account={account}
						isIdLocked={isAccountSaved(account.id)}
						onUpdate={(patch) => updateAccount(account.id, patch)}
						onDelete={() => setDeleteTarget({ type: "account", accountId: account.id })}
						onAddSource={() => addSource(account.id)}
						onUpdateSource={(sourceId, patch) => updateSource(account.id, sourceId, patch)}
						onDeleteSource={(sourceId) =>
							setDeleteTarget({
								type: "source",
								accountId: account.id,
								sourceId,
							})
						}
						getSyncStatus={(sourceId) => getSyncStatus(account.id, sourceId)}
					/>
				))}

				<Button onClick={addAccount} variant="outline" className="mb-6 max-md:h-11 max-md:w-full">
					<Plus className="mr-2 h-4 w-4" />
					Dodaj konto
				</Button>
			</div>

			{hasAnySources && <FileBrowser accounts={localAccounts} />}

			{hasAnySources && <DownloadHistory accounts={localAccounts} />}

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(open) => !open && setDeleteTarget(null)}
				title={deleteTarget?.type === "account" ? "Usun konto?" : "Usun zrodlo?"}
				description={
					deleteTarget?.type === "account"
						? "Konto i wszystkie jego zrodla zostana usuniete."
						: "Zrodlo zostanie trwale usuniete."
				}
				variant="destructive"
				confirmLabel="Usun"
				onConfirm={confirmDelete}
			/>
		</div>
	);
}

// ─── Account Card ───────────────────────────────────────────────────────────────

function AccountCard({
	account,
	isIdLocked,
	onUpdate,
	onDelete,
	onAddSource,
	onUpdateSource,
	onDeleteSource,
	getSyncStatus,
}: {
	account: IqviaAccount;
	isIdLocked: boolean;
	onUpdate: (patch: Partial<IqviaAccount>) => void;
	onDelete: () => void;
	onAddSource: () => void;
	onUpdateSource: (sourceId: string, patch: Partial<IqviaSource>) => void;
	onDeleteSource: (sourceId: string) => void;
	getSyncStatus: (sourceId: string) => SyncStatusEntry | undefined;
}) {
	const [password, setPassword] = useState("");

	const savePasswordMutation = useMutation({
		mutationFn: () =>
			apiFetch(`/admin/settings/iqvia_account_${account.id}`, {
				method: "PUT",
				body: JSON.stringify({ value: { password }, encrypted: true }),
			}),
		onSuccess: () => setPassword(""),
	});

	const testConnectionMutation = useMutation({
		mutationFn: () =>
			apiFetch<{ success: boolean; latencyMs?: number; error?: string }>(
				"/admin/iqvia/test-connection",
				{
					method: "POST",
					body: JSON.stringify({ accountId: account.id }),
				},
			),
	});

	const syncMutation = useMutation({
		mutationFn: (sourceId: string) =>
			apiFetch("/admin/iqvia/sync", {
				method: "POST",
				body: JSON.stringify({ accountId: account.id, sourceId }),
			}),
	});

	return (
		<Card className="mb-4">
			<CardHeader>
				<div className="flex items-center justify-between max-md:gap-2">
					<CardTitle className="max-md:min-w-0 max-md:break-words">
						Konto: {account.label || account.id}
					</CardTitle>
					<Button
						variant="ghost"
						size="sm"
						onClick={onDelete}
						className="text-destructive max-md:size-9"
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>
				<CardDescription>Konfiguracja konta MOVEit Transfer</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label>ID konta</Label>
						<Input
							value={account.id}
							onChange={(e) => onUpdate({ id: e.target.value })}
							placeholder="W"
							disabled={isIdLocked}
						/>
						{isIdLocked && (
							<p className="text-xs text-muted-foreground">ID nie mozna zmienic po zapisaniu</p>
						)}
					</div>
					<div className="space-y-2">
						<Label>Nazwa</Label>
						<Input
							value={account.label}
							onChange={(e) => onUpdate({ label: e.target.value })}
							placeholder="Apteki wlasne (W-)"
						/>
					</div>
				</div>

				<div className="space-y-2">
					<Label>URL serwera</Label>
					<Input
						value={account.baseUrl}
						onChange={(e) => onUpdate({ baseUrl: e.target.value })}
						placeholder={DEFAULT_BASE_URL}
					/>
				</div>

				<label className="flex items-center gap-2 text-sm cursor-pointer">
					<input
						type="checkbox"
						checked={account.acceptInvalidCerts ?? false}
						onChange={(e) => onUpdate({ acceptInvalidCerts: e.target.checked })}
						className="rounded border-input"
					/>
					Akceptuj nieprawidlowe certyfikaty TLS (self-signed)
				</label>

				<div className="space-y-2">
					<Label>Nazwa uzytkownika</Label>
					<Input
						value={account.username}
						onChange={(e) => onUpdate({ username: e.target.value })}
						placeholder="djfarmacja-w"
					/>
				</div>

				<div className="space-y-2">
					<Label>Haslo</Label>
					<div className="flex gap-2">
						<Input
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder="Wpisz nowe haslo..."
							className="flex-1"
						/>
						<Button
							onClick={() => savePasswordMutation.mutate()}
							disabled={savePasswordMutation.isPending || !password}
							size="sm"
							className="max-md:h-9"
						>
							{savePasswordMutation.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								"Zapisz haslo"
							)}
						</Button>
					</div>
					{savePasswordMutation.isSuccess && (
						<p className="text-xs text-[var(--success)] flex items-center gap-1">
							<CheckCircle2 className="h-3 w-3" />
							Haslo zapisane
						</p>
					)}
				</div>

				<div className="pt-2 flex items-center gap-3 max-md:flex-wrap">
					<Button
						variant="outline"
						onClick={() => testConnectionMutation.mutate()}
						disabled={testConnectionMutation.isPending}
						className="max-md:h-11 max-md:w-full"
					>
						{testConnectionMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						Testuj polaczenie
					</Button>
					{testConnectionMutation.data && (
						<span>
							{testConnectionMutation.data.success ? (
								<span className="text-sm text-[var(--success)] flex items-center gap-1">
									<CheckCircle2 className="h-4 w-4" />
									Polaczono
									{testConnectionMutation.data.latencyMs != null &&
										` (${testConnectionMutation.data.latencyMs}ms)`}
								</span>
							) : (
								<span className="text-sm text-destructive flex items-center gap-1 max-md:items-start max-md:break-words">
									<XCircle className="h-4 w-4 shrink-0" />
									{testConnectionMutation.data.error ?? "Blad polaczenia"}
								</span>
							)}
						</span>
					)}
				</div>

				<div className="border-t pt-4 mt-4">
					<h3 className="text-sm font-medium mb-3">Zrodla danych</h3>
					{account.sources.map((source) => {
						const status = getSyncStatus(source.id);
						return (
							<div key={source.id} className="border rounded-md p-3 mb-3 space-y-3">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
									<div className="space-y-1">
										<Label className="text-xs">ID zrodla</Label>
										<Input
											value={source.id}
											onChange={(e) =>
												onUpdateSource(source.id, {
													id: e.target.value,
												})
											}
											className="h-8 text-sm max-md:h-9"
											placeholder="chc"
											disabled={isIdLocked}
										/>
									</div>
									<div className="space-y-1">
										<Label className="text-xs">Nazwa</Label>
										<Input
											value={source.label}
											onChange={(e) =>
												onUpdateSource(source.id, {
													label: e.target.value,
												})
											}
											className="h-8 text-sm max-md:h-9"
											placeholder="Kategoryzacja CHC"
										/>
									</div>
								</div>
								<div className="space-y-1">
									<Label className="text-xs">Sciezka raportu</Label>
									<PathAutocomplete
										value={source.reportPath}
										onChange={(v) =>
											onUpdateSource(source.id, {
												reportPath: v,
											})
										}
										accountId={account.id}
									/>
								</div>
								<div className="flex items-center gap-3 max-md:flex-wrap">
									<div className="space-y-1 flex-1 max-md:min-w-[10rem]">
										<Label className="text-xs">Harmonogram</Label>
										<Select
											value={String(source.scheduleHours)}
											onValueChange={(v) =>
												onUpdateSource(source.id, {
													scheduleHours: Number(v),
												})
											}
										>
											<SelectTrigger className="h-8 text-sm">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{SCHEDULE_OPTIONS.map((opt) => (
													<SelectItem key={opt.value} value={opt.value}>
														{opt.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<div className="flex items-end gap-2 pt-4 max-md:pt-0">
										<Button
											variant="outline"
											size="sm"
											onClick={() => syncMutation.mutate(source.id)}
											disabled={syncMutation.isPending}
											className="max-md:h-9"
										>
											{syncMutation.isPending ? (
												<Loader2 className="h-3 w-3 animate-spin" />
											) : (
												<RefreshCw className="h-3 w-3" />
											)}
											<span className="ml-1">Synchronizuj teraz</span>
										</Button>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => onDeleteSource(source.id)}
											className="text-destructive max-md:size-9"
										>
											<Trash2 className="h-3 w-3" />
										</Button>
									</div>
								</div>
								{status?.lastSyncAt && (
									<p className="text-xs text-muted-foreground max-md:break-words">
										Ostatnia synchronizacja: {formatDate(status.lastSyncAt)}
										{status.lastResult ? ` — ${status.lastResult}` : ""}
									</p>
								)}
							</div>
						);
					})}
					<Button variant="outline" size="sm" onClick={onAddSource} className="max-md:h-9">
						<Plus className="mr-1 h-3 w-3" />
						Dodaj zrodlo
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}

// ─── File Browser ───────────────────────────────────────────────────────────────

/**
 * Match TYLKO globalne raporty decylowe (per-pharmacy mają unit_code prefix ze spacją,
 * jak "032973 RAPORT_DECYLOWY_*" — patrz backend `decyl_classifier::classify_decyl_filename`).
 * Per-pharmacy są filtrowane out z dialogu importu (Phase 2), więc link by tam prowadził
 * do pustego dialogu — review-fix Codex med-3.
 */
function isGlobalDecylFilename(name: string): boolean {
	return /^(?:\d+_)?RAPORT_DECYLOWY_\d{6}\.(?:txt|csv|xlsx|xls)$/i.test(name);
}

function FileBrowser({ accounts }: { accounts: IqviaAccount[] }) {
	const { user } = useAuth();
	// Link "Wczytaj do strateg" widoczny dla admin LUB super_admin (zgodnie z relax endpointu).
	const isSuperAdmin = !!user?.roles?.includes("super_admin") || !!user?.roles?.includes("admin");
	const [selectedAccount, setSelectedAccount] = useState(accounts[0]?.id ?? "");
	const [selectedSource, setSelectedSource] = useState("");
	const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
	const [downloadError, setDownloadError] = useState<string | null>(null);

	// Reset selected account if it no longer exists
	useEffect(() => {
		if (selectedAccount && !accounts.some((a) => a.id === selectedAccount)) {
			setSelectedAccount(accounts[0]?.id ?? "");
			setSelectedSource("");
		}
	}, [accounts, selectedAccount]);

	const currentAccount = accounts.find((a) => a.id === selectedAccount);
	const sources = currentAccount?.sources ?? [];

	const filesQuery = useQuery({
		queryKey: ["iqvia", "files", selectedAccount, selectedSource],
		queryFn: () => {
			const params = new URLSearchParams({
				account_id: selectedAccount,
				source_id: selectedSource,
			});
			return apiFetch<IqviaFile[]>(`/admin/iqvia/files?${params}`);
		},
		enabled: !!selectedAccount && !!selectedSource,
	});

	const handleDownload = async (file: IqviaFile) => {
		setDownloadingFile(file.id);
		setDownloadError(null);
		try {
			await downloadFileFromApi(file.id, file.name, selectedAccount, selectedSource);
			filesQuery.refetch();
		} catch (e: unknown) {
			const msg = e instanceof Error ? e.message : "Nieznany blad pobierania";
			setDownloadError(`Blad pobierania ${file.name}: ${msg}`);
		} finally {
			setDownloadingFile(null);
		}
	};

	return (
		<Card className="mb-4">
			<CardHeader>
				<CardTitle>Przegladarka plikow</CardTitle>
				<CardDescription>Przegladaj i pobieraj pliki z serwerow MOVEit</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="flex gap-3 items-end max-md:flex-wrap">
					<div className="space-y-1 flex-1 max-md:min-w-[9rem]">
						<Label className="text-xs">Konto</Label>
						<Select
							value={selectedAccount}
							onValueChange={(v) => {
								setSelectedAccount(v);
								setSelectedSource("");
							}}
						>
							<SelectTrigger className="h-8 text-sm">
								<SelectValue placeholder="Wybierz konto" />
							</SelectTrigger>
							<SelectContent>
								{accounts.map((a) => (
									<SelectItem key={a.id} value={a.id}>
										{a.label || a.id}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-1 flex-1 max-md:min-w-[9rem]">
						<Label className="text-xs">Zrodlo</Label>
						<Select value={selectedSource} onValueChange={setSelectedSource}>
							<SelectTrigger className="h-8 text-sm">
								<SelectValue placeholder="Wybierz zrodlo" />
							</SelectTrigger>
							<SelectContent>
								{sources.map((s) => (
									<SelectItem key={s.id} value={s.id}>
										{s.label || s.id}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<Button
						variant="outline"
						size="sm"
						onClick={() => filesQuery.refetch()}
						disabled={filesQuery.isFetching}
						className="max-md:h-9"
					>
						{filesQuery.isFetching ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<RefreshCw className="h-4 w-4" />
						)}
						<span className="ml-1">Odswiez</span>
					</Button>
				</div>

				{downloadError && (
					<p className="text-sm text-destructive flex items-center gap-1 max-md:items-start max-md:break-all">
						<XCircle className="h-4 w-4 shrink-0" />
						{downloadError}
					</p>
				)}

				{filesQuery.data && (
					<div className="border rounded-md overflow-hidden max-md:overflow-x-auto">
						<table className="w-full text-sm">
							<thead className="bg-muted/50">
								<tr>
									<th className="text-left p-2 font-medium">Nazwa pliku</th>
									<th className="text-left p-2 font-medium">Rozmiar</th>
									<th className="text-left p-2 font-medium">Data uploadu</th>
									<th className="text-left p-2 font-medium">Status</th>
									<th className="text-right p-2 font-medium">Akcja</th>
								</tr>
							</thead>
							<tbody>
								{filesQuery.data.length === 0 ? (
									<tr>
										<td colSpan={5} className="p-4 text-center text-muted-foreground">
											Brak plikow
										</td>
									</tr>
								) : (
									filesQuery.data.map((file) => (
										<tr key={file.id} className="border-t">
											<td className="p-2 font-mono text-xs">{file.name}</td>
											<td className="p-2">{formatBytes(file.size)}</td>
											<td className="p-2">{formatDate(file.uploadStamp)}</td>
											<td className="p-2">
												<FileStatusBadge file={file} />
											</td>
											<td className="p-2 text-right">
												<div className="flex items-center justify-end gap-1">
													{isSuperAdmin &&
														isGlobalDecylFilename(file.name) &&
														file.alreadyDownloaded && (
															<Button
																variant="ghost"
																size="sm"
																asChild
																title="Otwórz dialog wczytania raportu w Strateg Cenowy"
																className="max-md:h-9"
															>
																<Link to="/strateg-cenowy/wyniki" search={{ openImport: true }}>
																	Wczytaj do strateg →
																</Link>
															</Button>
														)}
													<Button
														variant="ghost"
														size="sm"
														onClick={() => handleDownload(file)}
														disabled={downloadingFile === file.id}
														className="max-md:h-9"
													>
														{downloadingFile === file.id ? (
															<Loader2 className="h-3 w-3 animate-spin" />
														) : (
															<Download className="h-3 w-3" />
														)}
														<span className="ml-1">
															{file.alreadyDownloaded ? "Pobierz ponownie" : "Pobierz"}
														</span>
													</Button>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function FileStatusBadge({ file }: { file: IqviaFile }) {
	if (file.alreadyDownloaded) {
		return (
			<span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
				Pobrany {file.downloadedAt ? formatDate(file.downloadedAt) : ""}
			</span>
		);
	}
	return (
		<span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-[var(--success)]/10 text-[var(--success)]">
			Nowy
		</span>
	);
}

// ─── Download History ───────────────────────────────────────────────────────────

function DownloadHistory({ accounts }: { accounts: IqviaAccount[] }) {
	const [filterAccount, setFilterAccount] = useState("all");

	const historyQuery = useQuery({
		queryKey: ["iqvia", "downloads", filterAccount],
		queryFn: () => {
			const params =
				filterAccount !== "all" ? `?${new URLSearchParams({ account_id: filterAccount })}` : "";
			return apiFetch<DownloadEntry[]>(`/admin/iqvia/downloads${params}`);
		},
	});

	const processMutation = useMutation({
		mutationFn: () =>
			apiFetch<{ success: boolean; message: string }>("/admin/iqvia/process-downloads", {
				method: "POST",
			}),
		onSuccess: (res) => toast.success(res.message),
		onError: (e) => toast.error(e instanceof Error ? e.message : "Nie udalo sie uruchomic"),
	});

	// Reczne wgranie pliku decylowego (np. raport jednej apteki z maila).
	// Trafia do tej samej kolejki co pobrania z MOVEit i DOPISUJE sie do raportu okresu.
	const [uploadAccount, setUploadAccount] = useState("");
	const [uploadSource, setUploadSource] = useState("");
	const [uploadFile, setUploadFile] = useState<File | null>(null);
	const uploadAccountId = uploadAccount || accounts[0]?.id || "";
	const sources = accounts.find((a) => a.id === uploadAccountId)?.sources ?? [];
	const effectiveSource = uploadSource || sources[0]?.id || "";

	const uploadMutation = useMutation({
		mutationFn: () => {
			const fd = new FormData();
			fd.append("account_id", uploadAccountId);
			fd.append("source_id", effectiveSource);
			if (uploadFile) fd.append("file", uploadFile);
			return apiFetch<{ downloadId: number; fileName: string; processingStarted: boolean }>(
				"/admin/iqvia/upload",
				{ method: "POST", body: fd },
			);
		},
		onSuccess: (res) => {
			toast.success(
				res.processingStarted
					? `Plik ${res.fileName} wgrany, import trwa w tle`
					: `Plik ${res.fileName} w kolejce, worker jest zajety, import przy nastepnym ticku`,
			);
			setUploadFile(null);
			historyQuery.refetch();
		},
		onError: (e) => toast.error(e instanceof Error ? e.message : "Nie udalo sie wgrac pliku"),
	});

	return (
		<Card className="mb-4">
			<CardHeader>
				<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
					<CardTitle className="flex items-center gap-2">
						<FileDown className="h-5 w-5" />
						Historia pobran
					</CardTitle>
					<Button
						variant="outline"
						size="sm"
						onClick={() => processMutation.mutate()}
						disabled={processMutation.isPending}
						title="Przetwarza pliki ze statusem 'downloaded' od razu, zamiast czekac na godzinowy worker"
						className="max-md:h-9"
					>
						{processMutation.isPending ? (
							<Loader2 className="h-3 w-3 animate-spin" />
						) : (
							<RefreshCw className="h-3 w-3" />
						)}
						<span className="ml-1">Przetworz pobrane teraz</span>
					</Button>
				</div>
				<CardDescription>Ostatnie 50 pobran plikow</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="w-48">
					<Select value={filterAccount} onValueChange={setFilterAccount}>
						<SelectTrigger className="h-8 text-sm">
							<SelectValue placeholder="Wszystkie konta" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">Wszystkie konta</SelectItem>
							{accounts.map((a) => (
								<SelectItem key={a.id} value={a.id}>
									{a.label || a.id}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="rounded-md border border-dashed p-3 space-y-2">
					<div className="text-sm font-medium">Wgraj plik decylowy recznie</div>
					<p className="text-xs text-muted-foreground">
						Np. raport jednej apteki przyslany mailem. Nazwa musi byc jak z IQVIA (np. „083376
						RAPORT_DECYLOWY_202607.txt”). Wiersze dopisuja sie do raportu za ten okres, inne apteki
						zostaja bez zmian.
					</p>
					<div className="flex flex-wrap items-end gap-2">
						<div className="w-40 max-md:w-full">
							<Label className="text-xs">Konto</Label>
							<Select
								value={uploadAccountId}
								onValueChange={(v) => {
									setUploadAccount(v);
									setUploadSource("");
								}}
							>
								<SelectTrigger className="h-8 text-sm">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{accounts.map((a) => (
										<SelectItem key={a.id} value={a.id}>
											{a.label || a.id}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="w-56 max-md:w-full">
							<Label className="text-xs">Zrodlo</Label>
							<Select value={effectiveSource} onValueChange={setUploadSource}>
								<SelectTrigger className="h-8 text-sm">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{sources.map((s) => (
										<SelectItem key={s.id} value={s.id}>
											{s.label || s.id}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<Input
							type="file"
							accept=".txt,.csv,.xlsx,.zip"
							className="h-8 w-72 text-xs max-md:h-9 max-md:w-full"
							onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
						/>
						<Button
							size="sm"
							onClick={() => uploadMutation.mutate()}
							disabled={!uploadFile || !effectiveSource || uploadMutation.isPending}
							className="max-md:h-11 max-md:w-full"
						>
							{uploadMutation.isPending ? (
								<Loader2 className="h-3 w-3 animate-spin" />
							) : (
								<Download className="h-3 w-3" />
							)}
							<span className="ml-1">Wgraj i przetworz</span>
						</Button>
					</div>
				</div>

				<div className="border rounded-md overflow-hidden max-md:overflow-x-auto">
					<table className="w-full text-sm">
						<thead className="bg-muted/50">
							<tr>
								<th className="text-left p-2 font-medium">Konto</th>
								<th className="text-left p-2 font-medium">Zrodlo</th>
								<th className="text-left p-2 font-medium">Nazwa pliku</th>
								<th className="text-left p-2 font-medium">Data pobrania</th>
								<th className="text-left p-2 font-medium">Kto</th>
								<th className="text-left p-2 font-medium">Status</th>
							</tr>
						</thead>
						<tbody>
							{!historyQuery.data || historyQuery.data.length === 0 ? (
								<tr>
									<td colSpan={6} className="p-4 text-center text-muted-foreground">
										Brak historii pobran
									</td>
								</tr>
							) : (
								historyQuery.data.map((entry) => (
									<tr key={entry.id} className="border-t">
										<td className="p-2">{entry.accountId}</td>
										<td className="p-2">{entry.sourceId}</td>
										<td className="p-2 font-mono text-xs">{entry.fileName}</td>
										<td className="p-2">{formatDate(entry.downloadedAt)}</td>
										<td className="p-2">{entry.downloadedBy}</td>
										<td className="p-2">
											{entry.status === "downloaded" || entry.status === "processed" ? (
												<span className="text-[var(--success)]" title={entry.status}>
													<CheckCircle2 className="h-3 w-3 inline" />
												</span>
											) : (
												<span
													className="text-destructive"
													title={entry.errorMessage || entry.status}
												>
													<XCircle className="h-3 w-3 inline" />
												</span>
											)}
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</CardContent>
		</Card>
	);
}

// ─── Path Autocomplete ──────────────────────────────────────────────────────────

interface FolderEntry {
	name: string;
	fullPath: string;
}

function PathAutocomplete({
	value,
	onChange,
	accountId,
}: {
	value: string;
	onChange: (v: string) => void;
	accountId: string;
}) {
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState(value);
	const inputRef = useRef<HTMLInputElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setInputValue(value);
	}, [value]);

	// Parent path to query for subfolders
	const parentPath = (() => {
		const trimmed = inputValue.trimEnd();
		if (trimmed.endsWith("/")) return trimmed.slice(0, -1);
		const lastSlash = trimmed.lastIndexOf("/");
		return lastSlash >= 0 ? trimmed.slice(0, lastSlash) : "";
	})();

	const foldersQuery = useQuery({
		queryKey: ["iqvia", "folders", accountId, parentPath],
		queryFn: () => {
			const params = new URLSearchParams({ account_id: accountId, path: parentPath });
			return apiFetch<FolderEntry[]>(`/admin/iqvia/folders?${params}`);
		},
		enabled: open && !!accountId,
		staleTime: 30_000,
	});

	const suggestions = (() => {
		if (!foldersQuery.data) return [];
		const trimmed = inputValue.trimEnd();
		if (trimmed.endsWith("/") || trimmed === "") return foldersQuery.data;
		const lastSlash = trimmed.lastIndexOf("/");
		const typed =
			lastSlash >= 0 ? trimmed.slice(lastSlash + 1).toLowerCase() : trimmed.toLowerCase();
		return foldersQuery.data.filter((f) => f.name.toLowerCase().includes(typed));
	})();

	const selectFolder = (folder: FolderEntry) => {
		// Append "/" to navigate into the selected folder
		const newValue = `${folder.fullPath}/`;
		setInputValue(newValue);
		onChange(folder.fullPath);
		setOpen(true);
		setTimeout(() => inputRef.current?.focus(), 50);
	};

	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	return (
		<div ref={containerRef} className="relative">
			<div className="flex gap-1">
				<Input
					ref={inputRef}
					value={inputValue}
					onChange={(e) => {
						// Auto-replace backslash with forward slash
						setInputValue(e.target.value.replace(/\\/g, "/"));
						setOpen(true);
					}}
					onFocus={() => setOpen(true)}
					onBlur={() => {
						setTimeout(() => {
							if (inputValue !== value) onChange(inputValue);
						}, 200);
					}}
					onKeyDown={(e) => {
						if (e.key === "/" && !open) setOpen(true);
						if (e.key === "Escape") setOpen(false);
					}}
					className="h-8 text-sm flex-1 max-md:h-9"
					placeholder="djfarmacja-home/CHC"
				/>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="h-8 px-2 max-md:size-9"
					onClick={() => setOpen(!open)}
					title="Przegladaj foldery"
				>
					<FolderOpen className="h-4 w-4" />
				</Button>
			</div>
			{open && (
				<div className="absolute z-50 top-9 left-0 w-full bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
					{foldersQuery.isLoading && (
						<div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
							<Loader2 className="h-3 w-3 animate-spin" />
							Ladowanie folderow...
						</div>
					)}
					{foldersQuery.isError && (
						<p className="p-2 text-sm text-destructive">
							Blad: {foldersQuery.error instanceof Error ? foldersQuery.error.message : "Nieznany"}
						</p>
					)}
					{foldersQuery.isSuccess && suggestions.length === 0 && (
						<p className="p-2 text-sm text-muted-foreground">Brak podfolderow</p>
					)}
					{suggestions.map((folder) => (
						<button
							type="button"
							key={folder.fullPath}
							className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent cursor-pointer max-md:min-h-9 max-md:text-left max-md:break-all"
							onMouseDown={(e) => {
								e.preventDefault();
								selectFolder(folder);
							}}
						>
							<FolderOpen className="h-3 w-3 shrink-0 text-muted-foreground" />
							{folder.name}
						</button>
					))}
				</div>
			)}
		</div>
	);
}
